"use client";

import React from "react";
import { SectionContentProps } from "../../../types";
import EditableText from "../../../core/EditableText";
import { LocationContent, buildFullAddress, generateMapEmbedUrl } from "../types";
import {
  PiMapPin,
  PiPhone,
  PiEnvelope,
  PiClock,
  PiCar,
  PiLink,
} from "react-icons/pi";

// ============================================================
// VARIANT 2: SPLIT LOCATION
// Map and info side by side layout
// ============================================================

interface SplitLocationProps extends Omit<SectionContentProps, "content"> {
  content: LocationContent;
}

export default function SplitLocation({ content: propContent, onContentChange, isSelected }: SplitLocationProps) {
  const content = propContent || {};
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  const fullAddress = buildFullAddress(content);
  const mapEmbedUrl = content.mapEmbedUrl || generateMapEmbedUrl(fullAddress, content.mapProvider || "google");

  const handleGetDirections = () => {
    const destination = encodeURIComponent(fullAddress);
    window.open(`https://www.google.com/maps/dir/?destination=${destination}`, "_blank");
  };

  const handleMapUrlEdit = () => {
    const url = prompt("Enter Google Maps Embed URL:", mapEmbedUrl);
    if (url) onContentChange?.({ ...content, mapEmbedUrl: url });
  };

  return (
    <section className="location-section-split" aria-label="Location information">
      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
            <PiMapPin className="w-4 h-4" />
            <EditableText
              value={content.subtitle || "Get Directions"}
              onChange={(v) => onContentChange?.({ ...content, subtitle: v })}
              as="span"
              placeholder="Subtitle"
            />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
            <EditableText
              value={content.title || "Our Location"}
              onChange={(v) => onContentChange?.({ ...content, title: v })}
              as="span"
              placeholder="Title"
            />
          </h2>
          {content.description && (
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              <EditableText
                value={content.description}
                onChange={(v) => onContentChange?.({ ...content, description: v })}
                as="span"
                placeholder="Description"
              />
            </p>
          )}
        </div>

        {/* Split Layout: Map + Info */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Map */}
          <div className="order-2 md:order-1">
            <div className="bg-slate-100 rounded-2xl overflow-hidden shadow-lg h-[350px] md:h-[450px] relative">
              {fullAddress || content.mapEmbedUrl ? (
                <>
                  <iframe
                    src={mapEmbedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Map showing location"
                    aria-label="Interactive map"
                  />
                  {isSelected && (
                    <button
                      onClick={handleMapUrlEdit}
                      className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg text-xs font-medium shadow-lg hover:bg-white transition-colors flex items-center gap-1"
                    >
                      <PiLink className="w-3 h-3" />
                      Edit URL
                    </button>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-slate-200">
                  <PiMapPin className="w-10 h-10 text-slate-400 mb-3" />
                  <p className="text-slate-500 text-sm">Add address to show map</p>
                </div>
              )}
            </div>
          </div>

          {/* Location Info Card */}
          <div className="order-1 md:order-2 flex flex-col justify-center">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
              {/* Location Name / Header */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <PiMapPin className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    <EditableText
                      value={content.city ? `${content.city} Office` : "Main Office"}
                      onChange={(v) => {
                        const parts = v.split(" ");
                        onContentChange?.({ ...content, city: parts[0] });
                      }}
                      as="span"
                      placeholder="Location name"
                    />
                  </h3>
                  {content.country && (
                    <p className="text-slate-500 text-sm">
                      <EditableText
                        value={content.country}
                        onChange={(v) => onContentChange?.({ ...content, country: v })}
                        as="span"
                        placeholder="Country"
                      />
                    </p>
                  )}
                </div>
              </div>

              {/* Address */}
              {fullAddress && (
                <div className="mb-6">
                  <p className="text-xs text-slate-500 mb-2 uppercase tracking-wide font-medium">Address</p>
                  <p className="text-slate-900 leading-relaxed">
                    <EditableText
                      value={fullAddress}
                      onChange={(v) => {
                        const parts = v.split(",").map((s) => s.trim());
                        onContentChange?.({
                          ...content,
                          address: parts[0] || "",
                          city: parts[1] || "",
                          state: parts[2] || "",
                          postalCode: parts[3] || "",
                          country: parts[4] || "",
                        });
                      }}
                      as="span"
                      placeholder="Full address"
                    />
                  </p>
                </div>
              )}

              {/* Contact Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {content.showPhone !== false && (
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <PiPhone className="w-5 h-5 text-green-600" />
                      <span className="text-xs text-slate-500 uppercase tracking-wide font-medium">Phone</span>
                    </div>
                    <a
                      href={content.phone ? `tel:${content.phone}` : "#"}
                      className="font-semibold text-slate-900 hover:text-blue-600 transition-colors"
                    >
                      <EditableText
                        value={content.phone || "Add phone"}
                        onChange={(v) => onContentChange?.({ ...content, phone: v })}
                        as="span"
                        placeholder="Phone"
                      />
                    </a>
                  </div>
                )}

                {content.showEmail !== false && (
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <PiEnvelope className="w-5 h-5 text-purple-600" />
                      <span className="text-xs text-slate-500 uppercase tracking-wide font-medium">Email</span>
                    </div>
                    <a
                      href={content.email ? `mailto:${content.email}` : "#"}
                      className="font-semibold text-slate-900 hover:text-blue-600 transition-colors break-all"
                    >
                      <EditableText
                        value={content.email || "Add email"}
                        onChange={(v) => onContentChange?.({ ...content, email: v })}
                        as="span"
                        placeholder="Email"
                      />
                    </a>
                  </div>
                )}
              </div>

              {/* Business Hours */}
              {content.showHours && content.hours && content.hours.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <PiClock className="w-5 h-5 text-orange-600" />
                    <span className="font-semibold text-slate-900">Business Hours</span>
                  </div>
                  <div className="space-y-2">
                    {content.hours.map((hour, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"
                      >
                        <span className="text-slate-600 text-sm">{hour.day}</span>
                        <span className={hour.isClosed ? "text-red-500 text-sm" : "text-slate-900 font-medium text-sm"}>
                          {hour.isClosed ? "Closed" : `${hour.open} - ${hour.close}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA Button */}
              {content.showGetDirections !== false && (
                <button
                  onClick={handleGetDirections}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors"
                >
                  <PiCar className="w-5 h-5" />
                  <EditableText
                    value={content.ctaText || "Get Directions"}
                    onChange={(v) => onContentChange?.({ ...content, ctaText: v })}
                    as="span"
                    placeholder="Button text"
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
